// Compare page. A side-by-side per-skill AVERAGE table for up to 5 columns, each column an independent
// party: a Rep, or a whole Team / Region / Manager / Class. The comparison is configured in the right-rail
// builder (Column A–E → kind → item), stored server-side, and recomputed on each load so it stays current.
// Fails quiet.

import * as api from '../lib/api.js';
import { skillLabelById } from '../lib/skills.js';
import { navigateTo } from './sidenav.js';
import { parseZip, scoreSetAuto, averageSide, MAX_PER_SET } from '../lib/cohortScore.js';

function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

const fmt = (v: number | null): string => (v === null ? '—' : v.toFixed(1));

// The time window applied to the active comparison. 'all' = every scored call on record (bounded only by
// the tenant's 24-month retention). A view control, not stored server-side.
const COMPARE_WINDOWS: { token: string; label: string }[] = [
  { token: 'all', label: 'All time' },
  { token: '7d', label: 'Last 7 days' },
  { token: '30d', label: 'Last 30 days' },
  { token: '90d', label: 'Last 90 days' },
  { token: '6m', label: 'Last 6 months' },
  { token: '12m', label: 'Last 12 months' },
];
let compareWindow = 'all';

// The compared column kinds, in menu order. `list` maps the trends filters to {id,name} options.
type Opt = { id: string; name: string };
const KINDS: { kind: api.CompareMode; label: string; list: (f: api.TrendsFilters) => Opt[] }[] = [
  { kind: 'reps', label: 'Rep', list: (f) => (f.reps ?? []).map((r) => ({ id: r.key, name: r.name })) },
  { kind: 'teams', label: 'Team', list: (f) => (f.teams ?? []).map((t) => ({ id: t.id, name: t.name })) },
  { kind: 'regions', label: 'Region', list: (f) => (f.regions ?? []).map((r) => ({ id: r.id, name: r.name })) },
  { kind: 'managers', label: 'Manager', list: (f) => (f.managers ?? []).map((m) => ({ id: m.id, name: m.name })) },
  { kind: 'classes', label: 'Class', list: (f) => (f.classes ?? []).map((c) => ({ id: c.id, name: c.name })) },
];
const COL_LABELS = ['Column A', 'Column B', 'Column C', 'Column D', 'Column E'];

// Builder state: exactly MAX_COMPARE columns; a column with kind '' is unset. A column can also be a 'zip'
// (a set of transcripts imported + scored in-browser). Kept in module state so the rail survives re-renders;
// the active (stored) comparison is seeded into the first columns on load.
type ZipData = { name: string; transcripts: { name: string; text: string }[] };
type Col = { kind: '' | api.CompareMode | 'zip'; ref: string; zip?: ZipData };
const columns: Col[] = Array.from({ length: api.MAX_COMPARE }, () => ({ kind: '', ref: '' }));
let filters: api.TrendsFilters | null = null;

// When a comparison includes ZIP columns, ALL sides are computed in-browser (each ZIP auto-classified +
// scored; stored rep/entity columns fetched) and merged in column order. The merged array is rendered
// directly and is ephemeral (lost on reload) — the stored comparison holds only the non-ZIP columns.
let mixedSides: api.CompareSide[] | null = null;

function seedColumns(cols: api.CompareColumn[]): void {
  for (let i = 0; i < columns.length; i++) {
    const c = cols[i];
    columns[i] = c ? { kind: c.kind, ref: c.ref } : { kind: '', ref: '' };
  }
}

export function renderCompare(): void {
  const main = document.getElementById('compare-app');
  const rail = document.getElementById('compare-rail');
  if (!main) return;
  wireRailToggle();
  main.textContent = '';
  main.appendChild(h('p', 'tt-loading', 'Loading…'));
  // A mixed (ZIP-inclusive) result lives in module state; skip the stored fetch and render it directly.
  const compareP: Promise<api.CompareData> = mixedSides ? Promise.resolve({ empty: true }) : api.getCompare(compareWindow);
  Promise.all([compareP, api.getTrendsFilters().catch(() => null)])
    .then(([c, f]) => {
      filters = f;
      if (!mixedSides && !('empty' in c)) seedColumns(c.columns);
      if (rail) renderRail(rail);
      if (mixedSides) renderSides(main, mixedSides);
      else paintMain(main, c);
    })
    .catch(() => {
      main.textContent = '';
      main.appendChild(h('p', 'tt-empty', 'Compare is unavailable right now. Please try again.'));
    });
}

/** Right-edge chevron that collapses/shows the config + legend rail (so the matrix can use the full width
 *  for 5 columns). State persists per browser in localStorage; applied on every render, bound once. */
const RAIL_HIDDEN_KEY = 'studio.cmp.railHidden';
function wireRailToggle(): void {
  const btn = document.getElementById('cmp-rail-toggle');
  const layout = document.querySelector<HTMLElement>('.view[data-view="compare"] .tr-layout');
  if (!btn || !layout) return;
  const apply = (hidden: boolean): void => {
    layout.classList.toggle('rail-collapsed', hidden);
    btn.setAttribute('aria-expanded', String(!hidden));
    btn.setAttribute('aria-label', hidden ? 'Show the build panel' : 'Hide the build panel');
    btn.setAttribute('title', hidden ? 'Show panel' : 'Hide panel');
    const ico = btn.querySelector('.cmp-rail-toggle-ico');
    if (ico) ico.textContent = hidden ? '‹' : '›';
  };
  let hidden = false;
  try { hidden = localStorage.getItem(RAIL_HIDDEN_KEY) === '1'; } catch { /* private mode etc. */ }
  apply(hidden);
  if (btn.dataset.wired) return; // bind the click exactly once
  btn.dataset.wired = '1';
  btn.addEventListener('click', () => {
    const next = !layout.classList.contains('rail-collapsed');
    apply(next);
    try { localStorage.setItem(RAIL_HIDDEN_KEY, next ? '1' : '0'); } catch { /* ignore */ }
  });
}

function paintMain(main: HTMLElement, c: api.CompareData): void {
  main.textContent = '';
  if ('empty' in c) {
    const empty = h('div', 'cmp-empty');
    empty.appendChild(h('p', 'cmp-empty-h', 'No active comparison.'));
    empty.appendChild(h('p', 'cmp-empty-note', 'Pick at least two columns on the right (each a rep, team, region, manager, or class), then Compare.'));
    main.appendChild(empty);
    return;
  }
  renderSides(main, c.sides);
}

/** Render the insights strip + matrix from an explicit sides array (stored or mixed/ZIP). */
function renderSides(main: HTMLElement, sides: api.CompareSide[]): void {
  main.textContent = '';
  const body = h('div', 'cmp-body');
  body.appendChild(insightsStrip(sides));
  body.appendChild(heatMatrix(sides));
  main.appendChild(body);
}

// ---- Right-rail builder: 5 columns, each kind + item ----

/** Is this column fully specified? A ZIP column needs ≥1 imported transcript; others need kind + item. */
function colComplete(c: Col): boolean {
  return c.kind === 'zip' ? (c.zip?.transcripts.length ?? 0) > 0 : c.kind !== '' && c.ref !== '';
}
/** How many columns are fully chosen. Need ≥2 to compare. */
function completeCount(): number {
  return columns.filter(colComplete).length;
}

function renderRail(rail: HTMLElement): void {
  rail.textContent = '';
  // Canonical rail/config card: .tr-rail-card (grey border, no brackets) + .tr-rail-h header — matches
  // the Average Scores / Trends rails. See docs/WEB-DESIGN-SYSTEM.md "App (CRO Zone) page structure".
  const card = h('div', 'tr-rail-card cmp-cfg');
  card.appendChild(h('div', 'tr-rail-h', 'Build comparison'));
  card.appendChild(h('p', 'cmp-cfg-sub', `Pick 2–5 columns. Each can be a rep, a group, or an imported .zip (up to ${MAX_PER_SET} transcripts).`));

  for (let i = 0; i < columns.length; i++) card.appendChild(columnRow(i, rail));

  const win = h('div', 'cmp-cfg-win');
  win.appendChild(h('span', 'cmp-cfg-win-label', 'Window'));
  win.appendChild(windowSelect((v) => { compareWindow = v; renderCompare(); }));
  card.appendChild(win);

  const actions = h('div', 'cmp-cfg-actions');
  const go = h('button', 'gold-btn cmp-cfg-go', 'Compare') as HTMLButtonElement;
  go.type = 'button';
  go.disabled = completeCount() < 2;
  const status = h('p', 'cmp-cfg-status');
  status.hidden = true;
  go.addEventListener('click', () => { void runCompare(go, status); });
  const clear = h('button', 'cmp-cfg-clear', 'Clear') as HTMLButtonElement;
  clear.type = 'button';
  clear.addEventListener('click', () => {
    for (let i = 0; i < columns.length; i++) columns[i] = { kind: '', ref: '' };
    mixedSides = null;
    clear.disabled = true;
    compareWindow = 'all';
    api.clearCompare().then(() => renderCompare()).catch(() => { clear.disabled = false; });
  });
  actions.appendChild(go);
  actions.appendChild(clear);
  card.appendChild(actions);
  card.appendChild(status);

  // Stack the config card + a Legend card in the canonical rail wrapper (.trf-rail-inner).
  const inner = h('div', 'trf-rail-inner');
  inner.appendChild(card);
  inner.appendChild(legendCard());
  rail.appendChild(inner);
}

/** Run the comparison. ZIP columns are scored in-browser (auto-classify each transcript); the total is
 *  credit-checked FIRST and paused with a popup if the account can't cover it. Non-ZIP columns are stored
 *  + fetched, then everything merges in column order. A no-ZIP comparison takes the plain stored path. */
async function runCompare(go: HTMLButtonElement, status: HTMLElement): Promise<void> {
  const picked = columns.map((c, i) => ({ c, i })).filter((x) => colComplete(x.c));
  if (picked.length < 2) return;
  go.disabled = true;

  const zipCols = picked.filter((x) => x.c.kind === 'zip');
  const totalZip = zipCols.reduce((n, x) => n + (x.c.zip?.transcripts.length ?? 0), 0);

  // Plain stored path (no ZIP columns).
  if (totalZip === 0) {
    api.setCompare(picked.map((x) => ({ kind: x.c.kind as api.CompareMode, ref: x.c.ref }))).then(() => renderCompare()).catch(() => { go.disabled = false; });
    return;
  }

  // Credit pre-check: each imported transcript is a billable scored call, so confirm the account has
  // enough credit for the TOTAL before any scoring starts; otherwise pause and point to Billing.
  let creditsLeft = 0;
  try { creditsLeft = (await api.getAccount()).creditsLeft; } catch { creditsLeft = 0; }
  if (totalZip > creditsLeft) {
    openInsufficientCreditModal(totalZip, creditsLeft);
    go.disabled = completeCount() < 2;
    return;
  }

  // Mixed path: score each ZIP column (global progress), fetch stored sides, merge in column order.
  const controller = new AbortController();
  status.hidden = false;
  status.textContent = `Scoring ${totalZip} call${totalZip === 1 ? '' : 's'}…`;
  try {
    let doneAll = 0;
    const zipSideByCol = new Map<number, api.CompareSide>();
    for (const x of zipCols) {
      const zip = x.c.zip!;
      const aggs = await scoreSetAuto(zip.transcripts, controller.signal, (done) => { status.textContent = `Scoring… ${doneAll + done}/${totalZip}`; });
      doneAll += zip.transcripts.length;
      const name = zip.name.replace(/\.zip$/i, '') || `Imported set ${x.i + 1}`;
      zipSideByCol.set(x.i, averageSide(`zip:${x.i}`, name, aggs));
    }
    const storedCols = picked.filter((x) => x.c.kind !== 'zip');
    let storedSides: api.CompareSide[] = [];
    if (storedCols.length) {
      await api.setCompare(storedCols.map((x) => ({ kind: x.c.kind as api.CompareMode, ref: x.c.ref })));
      const data = await api.getCompare(compareWindow);
      storedSides = 'empty' in data ? [] : data.sides;
    }
    const merged: api.CompareSide[] = [];
    let si = 0;
    for (const x of picked) {
      if (x.c.kind === 'zip') { const s = zipSideByCol.get(x.i); if (s) merged.push(s); }
      else if (si < storedSides.length) merged.push(storedSides[si++]!);
    }
    mixedSides = merged;
    renderCompare();
  } catch (e) {
    status.hidden = true;
    go.disabled = completeCount() < 2;
    // eslint-disable-next-line no-alert
    alert(e instanceof Error ? e.message : 'Scoring failed. Please try again.');
  }
}

/** Popup: not enough credit to score the imported files. Points the user to Billing & Plans. */
function openInsufficientCreditModal(need: number, have: number): void {
  const overlay = h('div', 'cmp-modal-overlay');
  const modal = h('div', 'cmp-modal');
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.appendChild(h('div', 'cmp-modal-h', 'Insufficient credit'));
  modal.appendChild(h('p', 'cmp-modal-body', `Importing these files would score ${need} call${need === 1 ? '' : 's'}, but this account has ${Math.max(0, have)} credit${have === 1 ? '' : 's'} left. Please add credit in Billing & Plans, then try again.`));
  const row = h('div', 'cmp-modal-actions');
  const cancel = h('button', 'cmp-modal-cancel', 'Close') as HTMLButtonElement;
  cancel.type = 'button';
  const goBill = h('button', 'gold-btn', 'Go to Billing & Plans') as HTMLButtonElement;
  goBill.type = 'button';
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
  function close(): void { overlay.remove(); document.removeEventListener('keydown', onKey); }
  cancel.addEventListener('click', close);
  goBill.addEventListener('click', () => { close(); navigateTo('billing'); });
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  document.addEventListener('keydown', onKey);
  row.appendChild(cancel);
  row.appendChild(goBill);
  modal.appendChild(row);
  overlay.appendChild(modal);
  // Mount inside .app-region so the dark HUD CSS variables resolve (they're scoped to that region).
  (document.querySelector('.app-region') ?? document.body).appendChild(overlay);
  goBill.focus();
}

/** Legend / how-to-read card — explains the cell colours, the Δ column, and the Av-Score ring (so the
 *  ring caption can be dropped and the "wins skills but trails on score" question is answered up front). */
function legendCard(): HTMLElement {
  const card = h('div', 'tr-rail-card cmp-legend-card');
  card.appendChild(h('div', 'cmp-lg-h', 'How to read this'));

  const cells = h('div', 'cmp-lg-row');
  const sws = h('span', 'cmp-lg-sws');
  for (const c of ['#37c281', '#ffd21a', '#ff8c1f', '#ff5347']) {
    const i = document.createElement('i');
    i.style.background = c;
    sws.appendChild(i);
  }
  cells.appendChild(sws);
  const cellsTxt = h('span');
  cellsTxt.appendChild(strong('Cells'));
  cellsTxt.appendChild(document.createTextNode(' = each column’s average score for that skill (0–5): green ≥4, yellow ≥3, orange ≥2, red <2.'));
  cells.appendChild(cellsTxt);
  card.appendChild(cells);

  card.appendChild(legendRow('Δ', ' = the gap for a skill: the highest column minus the lowest.'));
  card.appendChild(legendRow('Ring', '’s number is the Av Score — each column’s average call score (0–100), weighted so critical skills count most. A column can lead on the skill cells yet trail on the ring.'));
  card.appendChild(legendRow('Averages', ' are over every scored call for that column in the chosen window; a skill not scored on a call is skipped.'));
  return card;
}

function strong(text: string): HTMLElement { return h('strong', undefined, text); }
function legendRow(label: string, rest: string): HTMLElement {
  const row = h('div', 'cmp-lg-row');
  const txt = h('span');
  txt.appendChild(strong(label));
  txt.appendChild(document.createTextNode(rest));
  row.appendChild(txt);
  return row;
}

const itemsFor = (kind: '' | api.CompareMode): Opt[] =>
  kind === '' || !filters ? [] : (KINDS.find((k) => k.kind === kind)?.list(filters) ?? []);

function columnRow(i: number, rail: HTMLElement): HTMLElement {
  const row = h('div', 'cmp-col');
  row.appendChild(h('label', 'cmp-col-label', COL_LABELS[i]));

  // Kind dropdown: the entity kinds + a final ZIP option (import a .zip of transcripts, scored in-browser).
  const kindOpts: Opt[] = [{ id: '', name: '—' }, ...KINDS.map((k) => ({ id: k.kind, name: k.label })), { id: 'zip', name: 'ZIP' }];
  const kindSel = customSelect({
    value: columns[i]!.kind,
    options: kindOpts,
    ariaLabel: `${COL_LABELS[i]} — what to compare`,
    onChange: (id) => {
      columns[i]!.kind = (id as Col['kind']) || '';
      columns[i]!.ref = '';
      delete columns[i]!.zip;
      if (columns[i]!.kind !== '' && columns[i]!.kind !== 'zip') {
        columns[i]!.ref = itemsFor(columns[i]!.kind as api.CompareMode)[0]?.id ?? ''; // adopt first item
      }
      renderRail(rail);
    },
  });
  kindSel.classList.add('cmp-col-kind');
  row.appendChild(kindSel);

  // Second control: a normal item dropdown, OR — for a ZIP column — an Import button.
  row.appendChild(columns[i]!.kind === 'zip' ? zipImport(i, rail) : itemDropdown(i, rail));
  return row;
}

/** The item dropdown for a rep/entity column. */
function itemDropdown(i: number, rail: HTMLElement): HTMLElement {
  const items = columns[i]!.kind === '' || columns[i]!.kind === 'zip' ? [] : itemsFor(columns[i]!.kind as api.CompareMode);
  const itemSel = customSelect({
    value: columns[i]!.ref,
    options: items,
    placeholder: columns[i]!.kind === '' ? 'Choose a kind first' : 'None available',
    disabled: columns[i]!.kind === '' || items.length === 0,
    ariaLabel: `${COL_LABELS[i]} — which one`,
    onChange: (id) => { columns[i]!.ref = id; renderRail(rail); },
  });
  itemSel.classList.add('cmp-col-item');
  return itemSel;
}

/** The ZIP "Import" control: a button that opens a .zip picker; after import it shows the call count and
 *  lets the user re-import. Up to MAX_PER_SET transcripts are kept. */
function zipImport(i: number, rail: HTMLElement): HTMLElement {
  const zip = columns[i]!.zip;
  const n = zip?.transcripts.length ?? 0;
  const btn = h('button', 'cmp-col-item cmp-col-import', n ? `${n} call${n === 1 ? '' : 's'} ✓` : 'Import .zip') as HTMLButtonElement;
  btn.type = 'button';
  btn.setAttribute('aria-label', `${COL_LABELS[i]} — import a .zip of transcripts`);
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.zip,application/zip';
  input.style.display = 'none';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    btn.textContent = 'Reading…';
    btn.disabled = true;
    parseZip(file)
      .then((parsed) => {
        const transcripts = parsed.transcripts.slice(0, MAX_PER_SET);
        columns[i]!.zip = { name: file.name, transcripts };
        renderRail(rail);
      })
      .catch((e) => { alert(e instanceof Error ? e.message : 'Could not read that .zip.'); btn.disabled = false; btn.textContent = 'Import .zip'; });
  });
  btn.addEventListener('click', () => input.click());
  const wrap = h('div', 'cmp-sel');
  wrap.appendChild(btn);
  wrap.appendChild(input);
  return wrap;
}

function windowSelect(onChange: (v: string) => void): HTMLElement {
  const el = customSelect({
    value: compareWindow,
    options: COMPARE_WINDOWS.map((w) => ({ id: w.token, name: w.label })),
    ariaLabel: 'Time window',
    onChange,
  });
  el.classList.add('cmp-win-select');
  return el;
}

/** A custom dark dropdown (button + floating panel), so the open list matches the app's dark HUD on every
 *  OS — native <select> popups are OS-drawn and can't be themed on macOS. Click / Escape / click-outside;
 *  the whole rail re-renders on select, which commits the value and closes this. */
function customSelect(opts: { value: string; options: Opt[]; placeholder?: string; disabled?: boolean; ariaLabel: string; onChange: (id: string) => void }): HTMLElement {
  const wrap = h('div', 'cmp-sel');
  const btn = h('button', 'cmp-sel-btn') as HTMLButtonElement;
  btn.type = 'button';
  btn.disabled = !!opts.disabled;
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-label', opts.ariaLabel);
  const current = opts.options.find((o) => o.id === opts.value);
  btn.appendChild(h('span', 'cmp-sel-label', current ? current.name : (opts.placeholder ?? 'Select')));
  btn.appendChild(h('span', 'cmp-sel-caret', '▾'));

  const panel = h('ul', 'cmp-sel-panel');
  panel.setAttribute('role', 'listbox');
  panel.hidden = true;
  for (const o of opts.options) {
    const li = h('li', `cmp-sel-opt${o.id === opts.value ? ' is-sel' : ''}`, o.name);
    li.setAttribute('role', 'option');
    li.addEventListener('click', () => { close(); opts.onChange(o.id); });
    panel.appendChild(li);
  }

  const onOutside = (e: MouseEvent): void => { if (!wrap.contains(e.target as Node)) close(); };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') { close(); btn.focus(); } };
  function open(): void {
    if (opts.disabled || !opts.options.length) return;
    panel.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('keydown', onKey);
  }
  function close(): void {
    panel.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', onOutside, true);
    document.removeEventListener('keydown', onKey);
  }
  btn.addEventListener('click', () => { if (panel.hidden) open(); else close(); });

  wrap.appendChild(btn);
  wrap.appendChild(panel);
  return wrap;
}

// ---- Results: insights strip + the compact matrix ----

/** A per-skill spread above this (1–5 scale) counts as a "major" gap — flagged in the matrix. */
const GAP_THRESHOLD = 1.5;

function insightsStrip(sides: api.CompareSide[]): HTMLElement {
  const strip = h('div', 'cmp-strip');
  const ins = computeInsights(sides);
  const row = h('div', 'cmp-strip-ins');
  row.appendChild(h('span', 'cmp-ins-tag', 'Insights'));
  if (!ins) {
    row.appendChild(h('span', 'cmp-ins-empty', 'Not enough scored calls yet to draw a comparison.'));
  } else {
    row.appendChild(h('span', 'cmp-ins-head', ins.headline));
    for (const b of ins.bullets) row.appendChild(h('span', 'cmp-ins-bullet', b));
  }
  strip.appendChild(row);
  return strip;
}

interface SkillRow { key: string; delta: number | null; vals: (number | null)[]; }

/** Skills ordered by cross-side spread (biggest gap first); Δ is null when <2 sides have a score. */
function gapSorted(sides: api.CompareSide[]): SkillRow[] {
  const canonical = sides[0]?.scores ?? [];
  const rows: SkillRow[] = canonical.map((sc, i) => {
    const vals = sides.map((s) => s.scores[i]?.avg ?? null);
    const present = vals.filter((v): v is number => v != null);
    const delta = present.length >= 2 ? Math.max(...present) - Math.min(...present) : null;
    return { key: sc.key, delta, vals };
  });
  return rows.sort((a, b) => (b.delta ?? -1) - (a.delta ?? -1));
}

/** A column-header gauge: name + call count above the average-call-score ring. */
function gaugeHead(side: api.CompareSide): HTMLElement {
  const th = h('th', 'cmp-mx-gh');
  th.appendChild(h('div', 'cmp-gauge-name', side.name));
  th.appendChild(h('div', 'cmp-gauge-sub', `${side.count} call${side.count === 1 ? '' : 's'}`));
  th.appendChild(gaugeRing(side.final));
  return th;
}

/** The comparison matrix: sides as columns (up to 5), skills as rows (biggest gap first). Each cell is
 *  the average score, shaded by band; the Δ column is the spread and flags major gaps (> GAP_THRESHOLD). */
function heatMatrix(sides: api.CompareSide[]): HTMLElement {
  const scroll = h('div', 'tt-scroll');
  const table = h('table', 'cmp-mx');
  const thead = h('thead');
  const htr = h('tr');
  htr.appendChild(h('th', 'cmp-mx-sk', ''));
  for (const side of sides) htr.appendChild(gaugeHead(side));
  const dth = h('th', 'cmp-mx-delta', 'Δ');
  dth.title = 'Spread — highest minus lowest across the columns';
  htr.appendChild(dth);
  thead.appendChild(htr);
  table.appendChild(thead);

  const tbody = h('tbody');
  for (const row of gapSorted(sides)) {
    const isGap = row.delta != null && row.delta > GAP_THRESHOLD;
    const tr = h('tr', isGap ? 'is-gap' : undefined);
    tr.appendChild(h('td', 'cmp-mx-sk', skillLabelById(row.key)));
    for (const v of row.vals) {
      const td = h('td', 'cmp-mx-cell', fmt(v));
      const hc = heatColor(v);
      if (hc) { td.style.background = hc.bg; td.style.color = hc.fg; }
      tr.appendChild(td);
    }
    tr.appendChild(h('td', `cmp-mx-delta${isGap ? ' is-gap' : ''}`, row.delta == null ? '—' : row.delta.toFixed(1)));
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  scroll.appendChild(table);
  return scroll;
}

/** Solid score band in the Benchmark palette (matches .bm-t*): 1–5 → green (≥4) / yellow (≥3) /
 *  orange (≥2) / red (<2), dark text. Null for no score. */
function heatColor(avg: number | null): { bg: string; fg: string } | null {
  if (avg == null) return null;
  const bg = avg >= 4 ? '#37c281' : avg >= 3 ? '#ffd21a' : avg >= 2 ? '#ff8c1f' : '#ff5347';
  return { bg, fg: '#0a0c0c' };
}

const SVG_NS = 'http://www.w3.org/2000/svg';
function svgCircle(r: number, cls: string): SVGCircleElement {
  const c = document.createElementNS(SVG_NS, 'circle');
  c.setAttribute('cx', '50');
  c.setAttribute('cy', '50');
  c.setAttribute('r', String(r));
  c.setAttribute('class', cls);
  return c;
}

/** The circular average-call-score gauge. `final` is on the 0–100 scale (null when no scored calls). */
function gaugeRing(final: number | null): HTMLElement {
  const wrap = h('div', 'cmp-gauge');
  const R = 42, C = 2 * Math.PI * R;
  const pct = final == null ? 0 : Math.max(0, Math.min(100, final)) / 100;
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('class', 'cmp-gauge-svg');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `Average call score ${final == null ? 'unavailable' : final.toFixed(1)}`);
  svg.appendChild(svgCircle(R, 'cmp-gauge-track'));
  const arc = svgCircle(R, 'cmp-gauge-arc');
  arc.setAttribute('stroke-dasharray', `${(C * pct).toFixed(2)} ${C.toFixed(2)}`);
  arc.setAttribute('transform', 'rotate(-90 50 50)');
  svg.appendChild(arc);
  wrap.appendChild(svg);
  const face = h('div', 'cmp-gauge-face');
  face.appendChild(h('div', 'cmp-gauge-num', final == null ? '—' : final.toFixed(1)));
  // The "Av Score" caption is intentionally omitted — explained once in the rail Legend card instead.
  wrap.appendChild(face);
  return wrap;
}

interface Insights {
  headline: string;
  bullets: string[];
}
const STRONG_DIFF = 0.4; // a per-skill lead (1–5 scale) big enough to call out as a differentiator

/** Explain WHY the leading side leads: the final-score gap as a %, plus the skills that drive it.
 *  Pure + exported for tests. Null when there aren't two sides with a final score. */
export function computeInsights(sides: api.CompareSide[]): Insights | null {
  const withFinal = sides.filter((s): s is api.CompareSide & { final: number } => s.final != null);
  if (withFinal.length < 2) return null;
  const sorted = [...withFinal].sort((a, b) => b.final - a.final);
  const leader = sorted[0]!;
  const runner = sorted[1]!;
  const pct = runner.final > 0 ? Math.round(((leader.final - runner.final) / runner.final) * 100) : null;

  const others = sorted.slice(1);
  const ups: { key: string; lead: number; other: number }[] = [];
  const downs: { key: string; lead: number; other: number }[] = [];
  leader.scores.forEach((sc, i) => {
    if (sc.avg == null) return;
    const otherVals = others.map((o) => o.scores[i]?.avg).filter((v): v is number => v != null);
    if (!otherVals.length) return;
    const otherMax = Math.max(...otherVals);
    if (sc.avg - otherMax >= STRONG_DIFF) ups.push({ key: sc.key, lead: sc.avg, other: otherMax });
    else if (otherMax - sc.avg >= STRONG_DIFF) downs.push({ key: sc.key, lead: sc.avg, other: otherMax });
  });
  ups.sort((a, b) => b.lead - b.other - (a.lead - a.other));

  const bullets: string[] = [];
  let headline: string;
  if (pct != null && pct <= 0) {
    headline = `${leader.name} and ${runner.name} are effectively tied on final score.`;
    if (ups.length) bullets.push(`${leader.name}'s clearest edge is ${skillLabelById(ups[0]!.key)} (${ups[0]!.lead.toFixed(1)} vs ${ups[0]!.other.toFixed(1)}).`);
  } else if (ups.length) {
    headline = pct != null ? `${leader.name} leads ${runner.name} by ~${pct}% on final score.` : `${leader.name} leads ${runner.name}.`;
    for (const d of ups.slice(0, 3)) bullets.push(`Strongest edge: ${skillLabelById(d.key)} (${d.lead.toFixed(1)} vs ${d.other.toFixed(1)}).`);
    for (const d of downs.slice(0, 2)) bullets.push(`Trails on ${skillLabelById(d.key)} (${d.lead.toFixed(1)} vs ${d.other.toFixed(1)}).`);
  } else {
    headline = pct != null ? `${leader.name} is ~${pct}% better than ${runner.name}.` : `${leader.name} edges ${runner.name}.`;
    bullets.push('No single skill drives it — the edge is small but consistent across every skill.');
    if (downs.length) bullets.push(`Only soft spot: ${skillLabelById(downs[0]!.key)} (${downs[0]!.lead.toFixed(1)} vs ${downs[0]!.other.toFixed(1)}).`);
  }
  return { headline, bullets };
}
